import { api, GDPR_DOCUMENT_TAG, isEndpointLockError } from '../index';

// Types for undo/redo operations
export interface UndoRedoRequest {
  documentId: string;
}

export interface UndoRedoResponse {
  success: boolean;
}

export interface DocumentHistoryRequest {
  documentId: string;
}

export interface DocumentHistoryResponse {
  undone: number;
  applied: number;
}

// Enhanced API with undo/redo endpoints
const undoRedoApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Undo last action on document
    undoDocument: builder.mutation<UndoRedoResponse, UndoRedoRequest>({
      query: ({ documentId }) => ({
        url: '/undo/document',
        method: 'POST',
        body: { documentId },
      }),
      // A lock refusal changed nothing, and refetching the document would re-open the AI processing failed dialog
      invalidatesTags: (_result, error) => (isEndpointLockError(error) ? [] : [GDPR_DOCUMENT_TAG]),
    }),

    // Redo last undone action on document
    redoDocument: builder.mutation<UndoRedoResponse, UndoRedoRequest>({
      query: ({ documentId }) => ({
        url: '/redo/document',
        method: 'POST',
        body: { documentId },
      }),
      invalidatesTags: (_result, error) => (isEndpointLockError(error) ? [] : [GDPR_DOCUMENT_TAG]),
    }),

    // Get document history to determine undo/redo availability
    getDocumentHistory: builder.query<DocumentHistoryResponse, DocumentHistoryRequest>({
      query: ({ documentId }) => ({
        url: '/document/history',
        method: 'GET',
        params: { documentId },
      }),
      providesTags: [GDPR_DOCUMENT_TAG],
    }),
  }),
});

export const {
  useUndoDocumentMutation,
  useRedoDocumentMutation,
  useGetDocumentHistoryQuery,
  useLazyGetDocumentHistoryQuery,
} = undoRedoApi;
